import { useQuery } from "@tanstack/react-query";
import { keycloakService } from "../../services/keycloakService";

// Define the external transfer interface based on the backend schema
export interface ExternalTransfer {
  extId: string;
  contactId: string;
  portfolioShortName: string;
  securityCode: string;
  units: string;
  createdAt: Date;
  updatedAt: Date;
  status: string;
  sourceAddress: string;
}

// Response type for the list of external transfers
export interface ListExternalTransferResponse {
  transfers: ExternalTransfer[];
}

/**
 * Fetches the list of external transfers for a specific contact
 */
async function fetchExternalTransfers(
  contactId?: string
): Promise<ExternalTransfer[]> {
  try {
    const token = await keycloakService.getToken();
    const url = new URL(process.env.REACT_APP_EXTERNAL_TRANSFERS_API_URL || "");

    // Add contactId as query parameter if provided
    if (contactId) {
      url.searchParams.append("contactId", contactId);
    }

    const response = await fetch(url.toString(), {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      throw new Error("Failed to fetch external transfers");
    }

    const data = (await response.json()) as ListExternalTransferResponse;
    return data.transfers;
  } catch (error) {
    console.error("Failed to fetch external transfers:", error);
    throw error;
  }
}

/**
 * Hook to get external transfers
 * @param contactId Optional contact ID to filter transfers
 */
export const useGetExternalTransfers = (contactId?: string) => {
  const {
    data: externalTransfers = [],
    isLoading: loading,
    refetch,
  } = useQuery({
    queryKey: ["externalTransfers", contactId],
    queryFn: () => fetchExternalTransfers(contactId),
    retry: false,
  });

  return { externalTransfers, loading, refetch };
};
